import type { ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { LegalPage, LegalSection } from '@/components/shared/LegalPage';
import { CodeBlock } from '@/components/shared/CodeBlock';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { BRAND } from '@/config/brand';
import {
  CALLS_PATH, CONTACTS_OUTPUT, CONTACTS_PATH, ENV_KEY, ENV_URL, ERROR_OUTPUT,
  apiBaseUrl, curlPage, exportEnv, nodeAllPages, nodeFirstPage,
} from '@/lib/apiExamples';

/**
 * /developers (DX-1, W4 B2-settings-apiKeys): the public guide to the API,
 * by task: authenticate, list contacts and calls, page through, read the
 * errors; webhooks are coming (W7). Examples read XDIAL_API_URL and
 * XDIAL_API_KEY from the environment. The full reference stays in Swagger
 * (/api/docs), for signed-in tenant admins.
 *
 * Every internal link is in DEVELOPERS_LINKS: Developers.test.tsx checks that
 * each one resolves to a route of App.tsx.
 */
export const DEVELOPERS_LINKS = {
  apiKeys: '/ParallelDialer/settings?section=apiKeys',
  terms: '/cgu',
  home: '/',
} as const;
export const OPENAPI_DOCS = '/api/docs';

const ERROR_CODES = [
  ['401', 'AUTH_REQUIRED'], ['401', 'INVALID_API_KEY'], ['403', 'FORBIDDEN'], ['403', 'SESSION_REQUIRED'],
  ['400', 'CONTRACT_VIOLATION'], ['400', 'INVALID_CURSOR'], ['400', 'LIMIT_TOO_HIGH'], ['400', 'SORT_FIELD_NOT_ALLOWED'],
] as const;

/*
 * QA-W6-13: <Trans> clones the elements of `components` with an
 * `i18nIsDynamicList` prop; a router <Link> or a bare <a> hands it to the
 * DOM (React warning). These wrappers take only what they use.
 */
function TransLink({ to, children }: { to: string; children?: ReactNode }) {
  return <Link to={to}>{children}</Link>;
}

function TransAnchor({ href, children }: { href: string; children?: ReactNode }) {
  return <a href={href} target="_blank" rel="noreferrer">{children}</a>;
}

export default function Developers() {
  const { t } = useTranslation(['pages']);
  useDocumentTitle(t('pages:developers.title'));
  const base = apiBaseUrl();
  const sections = ['auth', 'contacts', 'calls', 'pagination', 'errors', 'webhooks', 'reference'].map((id) => ({ id, heading: t(`pages:developers.${id}.heading`) }));
  const env = exportEnv(base);
  const link = (to: string) => <TransLink to={to} />;

  return (
    <LegalPage
      title={t('pages:developers.title')}
      lastUpdated={t('pages:developers.subtitle', { brand: BRAND.name })}
      sections={sections}
      related={{ to: DEVELOPERS_LINKS.terms, label: t('pages:developers.terms') }}
    >
      <LegalSection id="auth" heading={sections[0]!.heading}>
        <p><Trans t={t} i18nKey="pages:developers.auth.p1" components={{ keys: link(DEVELOPERS_LINKS.apiKeys) }} /></p>
        <p>{t('pages:developers.auth.p2')}</p>
        <CodeBlock code={env} label={t('pages:developers.labels.env')} />
        <CodeBlock code={curlPage()} label="curl" testId="developers-curl" />
      </LegalSection>

      <LegalSection id="contacts" heading={sections[1]!.heading}>
        <p>{t('pages:developers.contacts.p1', { path: CONTACTS_PATH })}</p>
        <CodeBlock code={nodeFirstPage(CONTACTS_PATH)} label={t('pages:developers.labels.node')} />
        <p>{t('pages:developers.labels.output')}</p>
        <CodeBlock code={CONTACTS_OUTPUT} label="JSON" />
      </LegalSection>

      <LegalSection id="calls" heading={sections[2]!.heading}>
        <p>{t('pages:developers.calls.p1', { path: CALLS_PATH })}</p>
        <CodeBlock code={curlPage({ path: CALLS_PATH, body: { filter: { call_status: 'completed' }, sort: '-created_date', limit: 20 } })} label="curl" />
      </LegalSection>

      <LegalSection id="pagination" heading={sections[3]!.heading}>
        <p>{t('pages:developers.pagination.p1')}</p>
        <p>{t('pages:developers.pagination.p2')}</p>
        <CodeBlock code={nodeAllPages(CONTACTS_PATH)} label={t('pages:developers.labels.node')} />
      </LegalSection>

      <LegalSection id="errors" heading={sections[4]!.heading}>
        <p>{t('pages:developers.errors.p1')}</p>
        <CodeBlock code={ERROR_OUTPUT} label="JSON" />
        <table className="w-full text-left text-[13px]">
          <caption className="sr-only">{t('pages:developers.errors.caption')}</caption>
          <thead>
            <tr><th scope="col" className="py-1 pr-3">{t('pages:developers.errors.status')}</th><th scope="col" className="py-1 pr-3">{t('pages:developers.errors.code')}</th><th scope="col" className="py-1">{t('pages:developers.errors.meaning')}</th></tr>
          </thead>
          <tbody>
            {ERROR_CODES.map(([status, code]) => (
              <tr key={code} className="border-t" style={{ borderColor: 'var(--vf-border)' }}>
                <td className="py-1.5 pr-3 font-mono">{status}</td>
                <td className="py-1.5 pr-3 font-mono">{code}</td>
                <td className="py-1.5">{t(`pages:developers.errors.codes.${code}`)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </LegalSection>

      <LegalSection id="webhooks" heading={sections[5]!.heading}>
        <p>{t('pages:developers.webhooks.p1')}</p>
      </LegalSection>

      <LegalSection id="reference" heading={sections[6]!.heading}>
        <p>
          <Trans t={t} i18nKey="pages:developers.reference.p1" components={{ docs: <TransAnchor href={OPENAPI_DOCS} /> }} />
        </p>
      </LegalSection>
    </LegalPage>
  );
}
